import angular from 'angular';
import moment from 'moment';

/**
 * Service module for bridging event logging.
 * This class provides methods to log events and retrieve logged events.
 * It uses either a bridge context or a local context based on availability.
 */
class EventLogBridgeServiceModule {
	constructor($signals, $timeout, $window) {
		this.$signals = $signals;
		this.$timeout = $timeout;
		this.$window = $window;
	}

	/**
	 * Checks if the bridge is available.
	 * @returns {boolean} True if the bridge is available, false otherwise.
	 */
	isBridgeAvailable() {
		return typeof this.$window.jsBridgeEventLog !== 'undefined';
	}

	/**
	 * Gets the bridge context.
	 * @returns {Object} The bridge context if available.
	 */
	getBridgeContext() {
		return window.jsBridgeEventLog;
	}

	/**
	 * Logs an event.
	 * @param {string} eventMessage - The message of the event.
	 * @param {string} eventType - The type of the event.
	 * @param {string} explanatoryNote - An explanatory note for the event.
	 * @param {string} eventReason - The reason for the event.
	 * @param {Object} metaData - Metadata associated with the event.
	 * @returns {string} The UUID of the logged event.
	 */
	logEvent(eventMessage, eventType, explanatoryNote, eventReason, metaData) {
		if (typeof eventType === "undefined") {
			console.error("Missing parameter: eventType");
			return;
		}

		let context;

		if (!this.isBridgeAvailable()) {
			context = new EventLogBridgeLocalContext();
		} else {
			context = this.getBridgeContext();
		}

		if (typeof metaData === "undefined") {
			metaData = {};
		}
		const metaDataJSON = JSON.stringify(metaData);

		if (typeof explanatoryNote === 'undefined') {
			explanatoryNote = '';
		}
		const eventUUID = context.logEvent(eventMessage, eventType, explanatoryNote, eventReason, metaDataJSON);

		this.$signals.emit('updateEventLog');

		return eventUUID;
	}

	/**
	 * Retrieves logged events.
	 * @returns {Array} An array of logged events.
	 */
	getEvents() {
		let context;

		if (!this.isBridgeAvailable()) {
			context = new EventLogBridgeLocalContext();
		} else {
			context = this.getBridgeContext();
		}

		let events = context.getEvents();
		events = JSON.parse(events);

		return events;
	}
}

/**
 * Local context for event logging when the bridge is not available.
 * This class provides methods to log events and retrieve logged events using session storage.
 */
class EventLogBridgeLocalContext {
	/**
	 * Logs an event in the local context.
	 * @param {string} eventMessage - The message of the event.
	 * @param {string} eventType - The type of the event.
	 * @param {string} explanatoryNote - An explanatory note for the event.
	 * @param {string} eventReason - The reason for the event.
	 * @param {string} metaDataJSON - Metadata associated with the event in JSON format.
	 * @returns {string} The UUID of the logged event.
	 */
	logEvent(eventMessage, eventType, explanatoryNote, eventReason, metaDataJSON) {
		let items = sessionStorage.getItem('EventLogBridgeLocalContext');

		if (typeof items === "undefined") {
			items = "{}";
		}

		items = JSON.parse(items);

		const uuid = angular.createUUID();
		const dateString = moment().format('YYYY-MM-DD[T]HH:mm:ss[Z]');

		if (items == null) {
			items = {};
		}

		if (typeof explanatoryNote === 'undefined') {
			explanatoryNote = '';
		}

		items[uuid] = {
			'eventMessage': eventMessage,
			'eventReason': eventReason,
			'eventType': eventType,
			'explanatoryNote': explanatoryNote,
			'metaDataJSON': metaDataJSON,
			'ts': dateString
		};

		sessionStorage.setItem('EventLogBridgeLocalContext', JSON.stringify(items));

		return uuid;
	}

	/**
	 * Retrieves logged events from the local context.
	 * @returns {string} A JSON string of logged events.
	 */
	getEvents() {
		let items = sessionStorage.getItem('EventLogBridgeLocalContext');

		if (typeof items === "undefined") {
			items = "{}";
		}

		return items;
	}
}

export default EventLogBridgeServiceModule;